import { memo, useCallback, useMemo, useRef } from 'react';
import { BaseEdge, EdgeLabelRenderer, useReactFlow, type EdgeProps } from '@xyflow/react';
import type { MapEdgeAnchor, MapEdgeType, MapEdgeWaypoint } from '../../lib/systemMap';
import { useSystemMapEdgeActions, type MapEdgeEndpoint } from './SystemMapEdgeActionsContext';
import {
  applySegmentDrag,
  buildSystemMapEdgePath,
  extractCornerWaypoints,
  getDraggableSegments,
  resolveEdgePolyline,
  systemMapEdgeLabelPosition,
} from './systemMapEdgePath';
import { nodeRowSize, snapFlowPointToNodeAnchor } from './systemMapNodeGeometry';

export type SystemMapEdgeData = {
  label?: string | null;
  edgeType: MapEdgeType;
  waypoints: MapEdgeWaypoint[];
  sourceNodeId: string;
  targetNodeId: string;
  animated?: boolean;
  stroke?: string;
  showConnectorLabels?: boolean;
};

function SystemMapEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data,
  selected,
  markerEnd,
  style,
}: EdgeProps) {
  const edgeData = (data ?? {}) as SystemMapEdgeData;
  const waypoints = edgeData.waypoints ?? [];
  const waypointsRef = useRef(waypoints);
  waypointsRef.current = waypoints;

  const polyline = useMemo(
    () =>
      resolveEdgePolyline(
        sourceX,
        sourceY,
        targetX,
        targetY,
        sourcePosition,
        targetPosition,
        waypoints,
      ),
    [sourceX, sourceY, targetX, targetY, sourcePosition, targetPosition, waypoints],
  );
  const polylineRef = useRef(polyline);
  polylineRef.current = polyline;

  const segments = useMemo(() => getDraggableSegments(polyline), [polyline]);

  const { screenToFlowPosition } = useReactFlow();
  const {
    onWaypointsChange,
    onWaypointsCommit,
    selectedWaypointIndex,
    onSelectWaypoint,
    onAnchorChange,
    onAnchorCommit,
    selectedEndpoint,
    onSelectEndpoint,
    getNodeRow,
  } = useSystemMapEdgeActions();

  const path = buildSystemMapEdgePath(
    sourceX,
    sourceY,
    targetX,
    targetY,
    waypoints,
    sourcePosition,
    targetPosition,
  );
  const label = systemMapEdgeLabelPosition(
    sourceX,
    sourceY,
    targetX,
    targetY,
    waypoints,
    sourcePosition,
    targetPosition,
  );

  const startSegmentDrag = useCallback(
    (segmentIndex: number, event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      onSelectEndpoint(id, null);
      onSelectWaypoint(id, segmentIndex);

      const segment = segments.find((s) => s.index === segmentIndex);
      if (!segment) return;

      const onMove = (e: MouseEvent) => {
        const pos = screenToFlowPosition({ x: e.clientX, y: e.clientY });
        const value = segment.orientation === 'horizontal' ? pos.y : pos.x;
        const nextPolyline = applySegmentDrag(polylineRef.current, segmentIndex, value);
        const corners = extractCornerWaypoints(nextPolyline);
        waypointsRef.current = corners;
        onWaypointsChange(id, corners);
      };

      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        onWaypointsCommit(id, waypointsRef.current);
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [
      id,
      onSelectEndpoint,
      onSelectWaypoint,
      onWaypointsChange,
      onWaypointsCommit,
      screenToFlowPosition,
      segments,
    ],
  );

  const startEndpointDrag = useCallback(
    (endpoint: MapEdgeEndpoint, event: React.MouseEvent) => {
      event.stopPropagation();
      event.preventDefault();
      onSelectWaypoint(id, null);
      onSelectEndpoint(id, endpoint);

      const nodeId = endpoint === 'source' ? edgeData.sourceNodeId : edgeData.targetNodeId;
      const nodeRow = getNodeRow(nodeId);
      if (!nodeRow) return;

      const { width, height } = nodeRowSize(nodeRow);
      let latestAnchor: MapEdgeAnchor | null = null;

      const onMove = (e: MouseEvent) => {
        const pos = screenToFlowPosition({ x: e.clientX, y: e.clientY });
        latestAnchor = snapFlowPointToNodeAnchor(nodeRow.x, nodeRow.y, width, height, pos.x, pos.y);
        onAnchorChange(id, endpoint, latestAnchor);
      };

      const onUp = () => {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
        if (latestAnchor) onAnchorCommit(id, endpoint, latestAnchor);
      };

      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
    },
    [
      edgeData.sourceNodeId,
      edgeData.targetNodeId,
      getNodeRow,
      id,
      onAnchorChange,
      onAnchorCommit,
      onSelectEndpoint,
      onSelectWaypoint,
      screenToFlowPosition,
    ],
  );

  const edgeStyle = {
    ...style,
    stroke: edgeData.stroke ?? style?.stroke,
    strokeWidth: selected ? 2.5 : style?.strokeWidth,
  };

  const endpoints: { key: MapEdgeEndpoint; x: number; y: number }[] = [
    { key: 'source', x: sourceX, y: sourceY },
    { key: 'target', x: targetX, y: targetY },
  ];

  return (
    <>
      <BaseEdge
        id={id}
        path={path}
        markerEnd={markerEnd}
        style={edgeStyle}
        interactionWidth={20}
      />
      {selected ? (
        <>
          {segments.map((segment) => {
            const active = selectedWaypointIndex === segment.index;
            const cursor = segment.orientation === 'horizontal' ? 'ns-resize' : 'ew-resize';
            const hitSize = Math.max(segment.length, 28);
            const isHorizontal = segment.orientation === 'horizontal';

            return (
              <g key={`${id}-seg-${segment.index}`}>
                <rect
                  x={isHorizontal ? Math.min(segment.x1, segment.x2) : segment.midX - 8}
                  y={isHorizontal ? segment.midY - 8 : Math.min(segment.y1, segment.y2)}
                  width={isHorizontal ? hitSize : 16}
                  height={isHorizontal ? 16 : hitSize}
                  fill="transparent"
                  style={{ cursor, pointerEvents: 'all' }}
                  onMouseDown={(event) => startSegmentDrag(segment.index, event)}
                />
                <rect
                  x={segment.midX - (isHorizontal ? 14 : 4)}
                  y={segment.midY - (isHorizontal ? 4 : 14)}
                  width={isHorizontal ? 28 : 8}
                  height={isHorizontal ? 8 : 28}
                  rx={2}
                  fill={active ? '#10b981' : '#ffffff'}
                  stroke={active ? '#059669' : '#64748b'}
                  strokeWidth={2}
                  style={{ cursor, pointerEvents: 'all' }}
                  onMouseDown={(event) => startSegmentDrag(segment.index, event)}
                />
              </g>
            );
          })}
          {endpoints.map(({ key, x, y }) => {
            const active = selectedEndpoint === key;
            return (
              <g key={`${id}-${key}-anchor`}>
                <circle
                  cx={x}
                  cy={y}
                  r={active ? 10 : 8}
                  fill={active ? '#0ea5e9' : '#ffffff'}
                  stroke={active ? '#0284c7' : '#64748b'}
                  strokeWidth={2}
                  style={{ cursor: 'grab', pointerEvents: 'all' }}
                  onMouseDown={(event) => startEndpointDrag(key, event)}
                />
              </g>
            );
          })}
        </>
      ) : null}
      {edgeData.label && edgeData.showConnectorLabels !== false ? (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${label.x}px, ${label.y}px)`,
              pointerEvents: 'none',
            }}
            className="px-1.5 py-0.5 text-[10px] rounded bg-white/90 dark:bg-gray-900/90 text-gray-600 dark:text-gray-300 border border-gray-200 dark:border-gray-600 shadow-sm"
          >
            {edgeData.label}
          </div>
        </EdgeLabelRenderer>
      ) : null}
    </>
  );
}

export default memo(SystemMapEdge);
